import type { RunView } from "../types";
import { type PredictionAction } from "../wasm";

export interface PendingRequest {
  resolve: (payload: unknown) => void;
  reject: (error: Error) => void;
  timer: number;
  runPrediction?: {
    base: RunView;
    action: PredictionAction;
    operationSeq: number;
    preStateHash: string;
    opId: string;
  };
}

export interface RunAckPayload {
  runAck: {
    opSeq: number;
    acceptedAt: number;
    revision: number;
    stateHash: string;
    events?: Array<{ type: string; atMs: number; payload?: Record<string, unknown> }>;
    randomWindowDelta?: RunView["randomWindow"];
    nextDeadline?: number;
    ended?: boolean;
  };
  snapshot?: RunView;
}

export type RunStateWithTimers = RunView["run"] & { nextBurnAt?: number };

export function isIdempotentMethod(method: string): boolean {
  return [
    "run.start",
    "run.resume",
    "run.slide",
    "run.lock_row",
    "run.skill",
    "run.chest",
    "run.retreat",
    "offline.claim",
    "offline.purchase",
    "offline.settings",
    "inventory.equip",
    "inventory.unequip",
    "inventory.lock",
    "inventory.enhance",
    "inventory.calibrate",
    "inventory.socket",
    "inventory.unsocket",
    "inventory.reforge",
    "inventory.refine",
    "inventory.sell",
    "skills.configure",
    "skills.unlock",
    "skills.respec",
    "shop.buy",
    "shop.refresh",
    "shop.exchange",
    "shop.exchange_crystal",
    "workshop.upgrade",
    "workshop.convert",
    "life.start",
    "life.claim",
    "life.stop",
    "redeem.code",
    "tasks.claim",
    "admin.grant",
    "admin.codes.create",
    "admin.registration.update",
    "player.heal",
    "player.settings",
    "room.create",
    "raid.match",
    "room.join",
    "room.invite",
    "room.ready",
    "room.start",
    "room.leave",
    "room.update",
    "room.kick",
    "room.owner.transfer",
    "room.result.ack",
    "room.slide",
    "room.skill",
    "room.retreat",
    "race.start",
    "race.slide",
    "race.draft",
    "race.skill",
    "ladder.register",
    "ladder.challenge",
    "ladder.purchase",
  ].includes(method);
}

export function isRunOperationMethod(method: string): boolean {
  return [
    "run.start",
    "run.slide",
    "run.lock_row",
    "run.skill",
    "run.chest",
    "run.retreat",
  ].includes(method);
}

export function predictionAction(method: string, payload: object): PredictionAction | null {
  const value = payload as Record<string, unknown>;
  switch (method) {
    case "run.slide":
      if (
        (value.axis === "row" || value.axis === "col") &&
        typeof value.index === "number" &&
        typeof value.distance === "number"
      ) {
        return {
          kind: "slide",
          axis: value.axis,
          index: value.index,
          distance: value.distance,
          automated: value.automated === true,
        };
      }
      return null;
    case "run.lock_row":
      return typeof value.row === "number" ? { kind: "lock_row", row: value.row } : null;
    case "run.skill":
      return typeof value.skill === "string" ? { kind: "skill", skill: value.skill } : null;
    case "run.chest":
      return { kind: "chest", open: value.open === true };
    default:
      return null;
  }
}

export function isRunAckPayload(payload: unknown): payload is RunAckPayload {
  if (!payload || typeof payload !== "object" || !("runAck" in payload)) return false;
  const ack = (payload as { runAck?: unknown }).runAck;
  return Boolean(
    ack &&
      typeof ack === "object" &&
      typeof (ack as { stateHash?: unknown }).stateHash === "string",
  );
}

export async function digestOperation(
  previous: string,
  opSeq: number,
  opId: string,
  stateHash: string,
): Promise<string> {
  const payload = new TextEncoder().encode(
    `${previous.toLowerCase()}:${opSeq}:${opId}:${stateHash.toLowerCase()}`,
  );
  return bytesHex(new Uint8Array(await crypto.subtle.digest("SHA-256", payload)));
}

export function hexBytes(value: string): Uint8Array {
  if (!/^[0-9a-f]{64}$/i.test(value)) return new Uint8Array();
  return Uint8Array.from({ length: value.length / 2 }, (_, index) =>
    Number.parseInt(value.slice(index * 2, index * 2 + 2), 16),
  );
}

export function bytesHex(value: Uint8Array): string {
  return Array.from(value, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

export function browserIsOffline(): boolean {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

export function wait(delayMs: number): Promise<void> {
  return new Promise((resolve) => window.setTimeout(resolve, delayMs));
}

export function randomUUID(): string {
  if (typeof crypto.randomUUID === "function") return crypto.randomUUID();
  const bytes = crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 0x0f) | 0x40;
  bytes[8] = (bytes[8] & 0x3f) | 0x80;
  const hex = Array.from(bytes, (value) => value.toString(16).padStart(2, "0"));
  return `${hex.slice(0, 4).join("")}-${hex.slice(4, 6).join("")}-${hex.slice(6, 8).join("")}-${hex.slice(8, 10).join("")}-${hex.slice(10).join("")}`;
}
